import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Header component — minimal React wrapper for the semantic <header> element.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - The `.header` base class applied automatically
 * - `data-component="Header"` for component identification (consumer can override)
 * - Ref forwarding to the underlying `<header>` element
 * - `className` merging via `cn()`
 * - Full HTML attribute passthrough (id, aria-label, aria-labelledby, role, etc.)
 *
 * Header is a layout primitive — flex shell + slot markers only. All
 * typography for heading-tier text lives in typography.css and is
 * applied via Typography's `text-*-header` classes (`text-page-header`,
 * `text-category-header`, `text-sub-header`, `text-card-header`,
 * `text-nav-header`) directly on the heading element inside.
 *
 * Three slot wrapper components ship alongside Header:
 * - `HeaderIcon`   — `<span class="header-icon">` (leading icon slot)
 * - `HeaderTitle`  — `<div class="header-title">` (title layout slot; drop a
 *                    `<Typography as="hN" className="text-*-header">` inside)
 * - `HeaderActions` — `<div class="header-actions">` (trailing actions slot)
 *
 * The wrappers are thin: each renders the element above with the matching slot
 * class auto-applied, plus `cn()` className merging, ref forwarding, full HTML
 * attribute passthrough, and a `data-component` identifier. The raw class-marker
 * pattern (`<Icon className="header-icon" />`, `<div className="header-actions">`)
 * still works — the wrappers exist for ergonomics, not as a contract.
 *
 * @example
 * ```tsx
 * // Page header with icon + title + actions
 * <Header>
 *   <HeaderIcon><LineChart aria-hidden /></HeaderIcon>
 *   <HeaderTitle>
 *     <Typography as="h1" className="text-page-header">Trends</Typography>
 *   </HeaderTitle>
 *   <HeaderActions>
 *     <Button>Add</Button>
 *   </HeaderActions>
 * </Header>
 *
 * // Category header with truncating title
 * <Header>
 *   <HeaderTitle>
 *     <Typography as="h2" className="text-category-header truncate">
 *       {table.title}
 *     </Typography>
 *   </HeaderTitle>
 * </Header>
 *
 * // Sub-section header — the Titan theme auto-paints a card-style background
 * // when it detects `.text-sub-header` in the children. No modifier class needed.
 * <Header>
 *   <HeaderTitle>
 *     <Typography as="h3" className="text-sub-header">Compressors</Typography>
 *   </HeaderTitle>
 * </Header>
 *
 * // With aria-labelledby recipe for screen reader naming
 * const titleId = React.useId();
 * <Header aria-labelledby={titleId}>
 *   <HeaderIcon><LineChart aria-hidden /></HeaderIcon>
 *   <HeaderTitle>
 *     <Typography as="h1" id={titleId} className="text-page-header">
 *       Facility Performance
 *     </Typography>
 *   </HeaderTitle>
 * </Header>
 *
 * // Raw class-marker pattern still works (escape hatch for unusual elements)
 * <Header>
 *   <LineChart className="header-icon" aria-hidden />
 *   <Typography as="h1" className="header-title text-page-header">Trends</Typography>
 *   <div className="header-actions"><Button>Add</Button></div>
 * </Header>
 *
 * // Override data-component for feature-scoped semantics
 * <Header data-component="DetailHeader" data-testid="detail-header">
 *   <HeaderTitle>
 *     <Typography as="h1" className="text-page-header">...</Typography>
 *   </HeaderTitle>
 * </Header>
 * ```
 */

export type HeaderProps = React.HTMLAttributes<HTMLElement>;

export const Header = React.forwardRef<HTMLElement, HeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <header ref={ref} data-component="Header" className={cn('header', className)} {...props}>
        {children}
      </header>
    );
  }
);

Header.displayName = 'Header';

/**
 * HeaderIcon — slot wrapper for a leading icon child inside `<Header>`.
 *
 * Renders a `<span>` with the `.header-icon` slot class auto-applied. The icon
 * inside (typically a lucide-react SVG) does not need any class of its own.
 * Equivalent to applying `className="header-icon"` directly to the icon element.
 */
export type HeaderIconProps = React.HTMLAttributes<HTMLSpanElement>;

export const HeaderIcon = React.forwardRef<HTMLSpanElement, HeaderIconProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        data-component="HeaderIcon"
        className={cn('header-icon', className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

HeaderIcon.displayName = 'HeaderIcon';

/**
 * HeaderTitle — slot wrapper for the title row inside `<Header>`.
 *
 * Renders a `<div>` with the `.header-title` slot class auto-applied. Drop a
 * `<Typography as="hN" className="text-*-header">` inside to supply both the
 * semantic heading element and the visual style — HeaderTitle does not impose a
 * heading level or any typography of its own. The slot class provides
 * `min-width: 0` and `flex: 1 1 auto`, so a `truncate` utility on the Typography
 * child works as expected.
 */
export type HeaderTitleProps = React.HTMLAttributes<HTMLDivElement>;

export const HeaderTitle = React.forwardRef<HTMLDivElement, HeaderTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-component="HeaderTitle"
        className={cn('header-title', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

HeaderTitle.displayName = 'HeaderTitle';

/**
 * HeaderActions — slot wrapper for the trailing action-row inside `<Header>`.
 *
 * Renders a `<div>` with the `.header-actions` slot class auto-applied. The
 * slot pins to the right via `margin-inline-start: auto`, uses a fixed action
 * height (`--header-actions-h`, default 2.25rem / 36px), and lays its children
 * out in a horizontal flex row with `gap: var(--inline-default)`.
 */
export type HeaderActionsProps = React.HTMLAttributes<HTMLDivElement>;

export const HeaderActions = React.forwardRef<HTMLDivElement, HeaderActionsProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-component="HeaderActions"
        className={cn('header-actions', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

HeaderActions.displayName = 'HeaderActions';
